import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Headphones, Film } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import RegionBlocked from "@/components/RegionBlocked";
import { LockBadge, AccessModal } from "@/components/LockedContent";
import { useMember } from "@/context/MemberContext";
import { useSeo } from "@/lib/useSeo";
import { renderEmphasis } from "@/lib/richText";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1550188225-af2e1c2529b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Healing() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [modal, setModal] = useState(false);
  const [content, setContent] = useState({});
  const { member } = useMember();

  useSeo({
    title: "Space for Healing Together",
    description: "A gentle, members-only space of guided audio, video and writing for healing — held by Gemma Veda.",
  });

  useEffect(() => {
    api.get("/settings").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/healing")
      .then(({ data }) => setEntries(data))
      .catch((err) => { if (err.response?.status === 451) setBlocked(true); })
      .finally(() => setLoading(false));
  }, []);

  if (blocked) return <RegionBlocked />;

  const heroImage = content.healing_image_url || HERO_FALLBACK;
  const eyebrow = content.healing_eyebrow || "A members' sanctuary";
  const heading = content.healing_heading || "Space for *Healing* Together";
  const subtext = content.healing_subtext || "Guided audio, gentle video and quiet writing — shared, by invitation, with those who walk this path alongside me.";

  return (
    <PublicLayout>
      <AccessModal open={modal} onClose={() => setModal(false)} />

      {/* Personalised, on-brand ocean hero */}
      <section data-testid="healing-page" className="relative h-[70vh] min-h-[460px] flex items-end overflow-hidden">
        <img src={heroImage} alt="Calm ocean at soft light" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E2A33]/80 via-[#0E2A33]/30 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-16 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <div className="text-[#E8D8B7] text-xs tracking-[0.34em] uppercase">{eyebrow}</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 text-white leading-[1.05] max-w-3xl" data-testid="healing-heading">
              {renderEmphasis(heading, "italic text-[#E8D8B7]")}
            </h1>
            <p className="mt-5 text-white/85 text-base md:text-lg max-w-xl font-light leading-relaxed">
              {subtext}
            </p>
            {!member && (
              <button onClick={() => setModal(true)} className="gv-btn-gold mt-8" data-testid="healing-request-access">
                Request access
              </button>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          {loading && <div className="text-center text-[#5C6670] py-16">Loading…</div>}
          {!loading && entries.length === 0 && (
            <p className="text-center font-serif text-2xl italic text-[#5C6670] py-16">The space is being prepared. Please return soon.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {entries.map((e, i) => (
              <motion.article
                key={e.id}
                data-testid={`healing-card-${e.slug}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="group"
              >
                <Link to={`/healing/${e.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-[#E5E5E5]">
                  {e.cover_image && <img src={e.cover_image} alt={e.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  {e.locked && <LockBadge className="absolute top-4 left-4 z-10 shadow" testId={`healing-lock-${e.slug}`} />}
                  <div className="absolute bottom-4 right-4 flex gap-2">
                    {e.audio_url && <span className="bg-white/90 text-[#1A1F24] rounded-full p-2"><Headphones className="w-4 h-4" /></span>}
                    {e.video_url && <span className="bg-white/90 text-[#1A1F24] rounded-full p-2"><Film className="w-4 h-4" /></span>}
                  </div>
                </Link>
                <h2 className="font-serif text-2xl md:text-3xl mt-5 text-[#1A1F24] leading-tight">
                  <Link to={`/healing/${e.slug}`} className="hover:text-[#A68545] transition-colors">{e.title}</Link>
                </h2>
                {e.excerpt && <p className="mt-3 text-[#5C6670] font-light leading-relaxed">{e.excerpt}</p>}
                <Link to={`/healing/${e.slug}`} className="inline-block mt-4 text-sm tracking-[0.2em] uppercase gv-link text-[#C5A059]" data-testid={`healing-open-${e.slug}`}>
                  Enter the space →
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
